import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../hooks/useTheme';
import { getWorkoutVideos, deleteWorkoutVideo } from '../services/firestore';
import { toast } from 'react-toastify';

const Workouts = () => {
    const { user } = useAuth();
    const { darkMode } = useTheme();
    const [workouts, setWorkouts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [playingVideo, setPlayingVideo] = useState(null);
    const [deletingVideo, setDeletingVideo] = useState(null);

    // Admin email - same as in Admin.js
    const ADMIN_EMAIL = 'emailjaspertan@gmail.com';
    const isAdmin = user && user.email === ADMIN_EMAIL;

    useEffect(() => {
        const fetchWorkouts = async () => {
            try {
                const workoutVideos = await getWorkoutVideos();
                setWorkouts(workoutVideos);
            } catch (error) {
                console.error('Error fetching workout videos:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchWorkouts();
    }, []);

    const categories = ['all', 'strength', 'cardio', 'flexibility', 'hiit'];

    const filteredWorkouts = selectedCategory === 'all'
        ? workouts
        : workouts.filter(workout => workout.category === selectedCategory);

    const handlePlayVideo = (workoutId) => {
        setPlayingVideo(workoutId);
    };

    const handleCloseVideo = () => {
        setPlayingVideo(null);
    };

    // Cloudinary configuration - same as Admin.js
    const CLOUDINARY_UPLOAD_PRESET = 'workout_videos';

    // Replace the existing deleteFromCloudinary function

    const deleteFromCloudinary = async (videoUrl, thumbnailUrl) => {
        try {
            // Extract public_id from Cloudinary URLs
            const extractPublicId = (url) => {
                if (!url) return null;
                const parts = url.split('/');
                const uploadIndex = parts.indexOf('upload');
                if (uploadIndex === -1) return null;

                // Get everything after 'upload/v{version}/' or 'upload/'
                let pathParts;
                if (parts[uploadIndex + 1].startsWith('v')) {
                    // Has version number: upload/v1234567890/path/file
                    pathParts = parts.slice(uploadIndex + 2);
                } else {
                    // No version: upload/path/file
                    pathParts = parts.slice(uploadIndex + 1);
                }

                const fullPath = pathParts.join('/');

                // Remove file extension
                return fullPath.replace(/\.[^/.]+$/, '');
            };

            const videoPublicId = extractPublicId(videoUrl);
            const thumbnailPublicId = extractPublicId(thumbnailUrl);

            console.log('Extracted public IDs:', { videoPublicId, thumbnailPublicId });

            const deletePromises = [];

            // Delete video from Cloudinary
            if (videoPublicId) {
                const videoDeleteUrl = `https://api.cloudinary.com/v1_1/dwgbc05ja/video/destroy`;
                deletePromises.push(
                    fetch(videoDeleteUrl, {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/x-www-form-urlencoded',
                        },
                        body: new URLSearchParams({
                            public_id: videoPublicId,
                            api_key: 'YOUR_API_KEY', // You need to add this
                            timestamp: Math.floor(Date.now() / 1000).toString(),
                            // signature: 'generated_signature' // This is the complex part
                        }),
                    })
                );
            }

            // Since client-side deletion is complex, let's use a simpler approach
            console.log('Files to delete from Cloudinary:', { videoPublicId, thumbnailPublicId });
            toast.info('Note: Files may remain in Cloudinary storage. Consider manual cleanup.');

        } catch (error) {
            console.error('Error deleting from Cloudinary:', error);
        }
    };

    const handleDeleteVideo = async (workout) => {
        // Show confirmation dialog
        const confirmDelete = window.confirm(
            `Are you sure you want to delete "${workout.name}"?\n\nThis will remove the video from your app but files may remain in Cloudinary storage.\n\nThis action cannot be undone.`
        );

        if (!confirmDelete) return;

        setDeletingVideo(workout.id);

        try {
            // Skip Cloudinary deletion for now
            console.log('Skipping Cloudinary deletion. Files to manually delete:');
            console.log('Video URL:', workout.videoUrl);
            console.log('Thumbnail URL:', workout.thumbnailUrl);

            // Delete from Firestore
            toast.info('Removing video from database...');
            await deleteWorkoutVideo(workout.id);

            // Remove from local state
            setWorkouts(prev => prev.filter(w => w.id !== workout.id));

            toast.success('Workout video deleted from app! 🗑️');
            toast.info('Note: Video files remain in Cloudinary storage');

        } catch (error) {
            console.error('Error deleting workout:', error);
            toast.error(`Failed to delete workout: ${error.message}`);
        } finally {
            setDeletingVideo(null);
        }
    };

    // Replace the entire WorkoutCard component with this updated version:

    const WorkoutCard = ({ workout }) => {
        const [thumbnailError, setThumbnailError] = useState(false);

        return (
            <div className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300`}>
                {/* Video Thumbnail Container */}
                <div className="relative aspect-video bg-gray-200 overflow-hidden">
                    {workout.thumbnailUrl || workout.videoUrl ? (
                        <>
                            {/* Use image element for thumbnails (works better on iOS Safari) */}
                            {workout.thumbnailUrl && !thumbnailError ? (
                                <img
                                    src={workout.thumbnailUrl}
                                    alt={workout.name}
                                    className="w-full h-full object-cover"
                                    onError={() => {
                                        console.log('Thumbnail failed, trying video:', workout.thumbnailUrl);
                                        setThumbnailError(true);
                                    }}
                                    loading="lazy"
                                />
                            ) : (
                                /* Fallback to video element if image fails or no thumbnail */
                                <video
                                    src={workout.videoUrl}
                                    className="w-full h-full object-cover"
                                    muted
                                    playsInline
                                    preload="metadata"
                                    onError={() => {
                                        console.log('Video fallback also failed:', workout.videoUrl);
                                    }}
                                    style={{ objectFit: 'cover' }}
                                />
                            )}
                        </>
                    ) : (
                        <div className={`w-full h-full flex items-center justify-center ${darkMode ? 'bg-gray-700' : 'bg-gray-300'}`}>
                            <div className="text-4xl">🎥</div>
                        </div>
                    )}

                    {/* Play Button Overlay */}
                    <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-30 opacity-0 hover:opacity-100 transition-opacity duration-300">
                        <button
                            onClick={() => handlePlayVideo(workout.id)}
                            className="bg-blue-600 hover:bg-blue-700 text-white rounded-full p-4 transition-colors duration-200 shadow-lg"
                            title={`Play ${workout.name}`}
                        >
                            <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z" />
                            </svg>
                        </button>
                    </div>

                    {/* Admin Delete Button */}
                    {isAdmin && (
                        <div className="absolute top-2 right-2">
                            <button
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteVideo(workout);
                                }}
                                disabled={deletingVideo === workout.id}
                                className={`p-2 rounded-full transition-colors shadow-lg ${deletingVideo === workout.id
                                    ? 'bg-gray-500 cursor-not-allowed'
                                    : 'bg-red-600 hover:bg-red-700 text-white'
                                    }`}
                                title="Delete workout video"
                            >
                                {deletingVideo === workout.id ? (
                                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                ) : (
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                )}
                            </button>
                        </div>
                    )}

                    {/* Duration Badge */}
                    {workout.duration && (
                        <div className="absolute bottom-2 right-2 bg-black bg-opacity-75 text-white text-xs px-2 py-1 rounded">
                            {workout.duration}
                        </div>
                    )}

                    {/* Difficulty Badge */}
                    {workout.difficulty && (
                        <div className={`absolute top-2 left-2 text-xs px-2 py-1 rounded font-medium ${workout.difficulty === 'beginner' ? 'bg-green-500 text-white' :
                            workout.difficulty === 'intermediate' ? 'bg-yellow-500 text-black' :
                                'bg-red-500 text-white'
                            }`}>
                            {workout.difficulty.toUpperCase()}
                        </div>
                    )}
                </div>

                {/* Card Content */}
                <div className="p-4">
                    <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'} mb-2`}>
                        {workout.name}
                    </h3>

                    {workout.description && (
                        <p className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'} mb-3 line-clamp-2`}>
                            {workout.description}
                        </p>
                    )}

                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <span className={`text-xs px-2 py-1 rounded-full ${darkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-600'
                                }`}>
                                {workout.category}
                            </span>
                            {workout.equipment && (
                                <span className={`text-xs px-2 py-1 rounded-full ${darkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-600'
                                    }`}>
                                    {workout.equipment}
                                </span>
                            )}
                        </div>

                        {workout.rating && (
                            <div className="flex items-center gap-1">
                                <span className="text-yellow-500">⭐</span>
                                <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                                    {workout.rating}
                                </span>
                            </div>
                        )}
                    </div>

                    {/* Admin Actions in Card Footer */}
                    {isAdmin && (
                        <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                            <div className="flex justify-between items-center">
                                <span className={`text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-600 dark:bg-purple-900 dark:text-purple-300`}>
                                    Admin
                                </span>
                                <button
                                    onClick={() => handleDeleteVideo(workout)}
                                    disabled={deletingVideo === workout.id}
                                    className={`text-xs px-3 py-1 rounded transition-colors ${deletingVideo === workout.id
                                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                        : 'bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-900 dark:text-red-300 dark:hover:bg-red-800'
                                        }`}
                                >
                                    {deletingVideo === workout.id ? 'Deleting...' : 'Delete'}
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    const VideoModal = ({ workout, onClose }) => (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg max-w-4xl w-full max-h-[90vh] overflow-hidden">
                <div className="flex justify-between items-center p-4 border-b dark:border-gray-700">
                    <h3 className={`text-lg font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                        {workout.name}
                    </h3>
                    <div className="flex items-center gap-2">
                        {/* Admin Delete Button in Modal */}
                        {isAdmin && (
                            <button
                                onClick={() => {
                                    handleDeleteVideo(workout);
                                    onClose(); // Close modal after deletion
                                }}
                                disabled={deletingVideo === workout.id}
                                className={`p-2 rounded-lg transition-colors ${deletingVideo === workout.id
                                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                                    : 'bg-red-600 hover:bg-red-700 text-white'
                                    }`}
                                title="Delete workout video"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                            </button>
                        )}
                        <button
                            onClick={onClose}
                            className={`p-2 rounded-lg ${darkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-100'}`}
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                <div className="aspect-video">
                    <video
                        src={workout.videoUrl}
                        controls
                        autoPlay
                        className="w-full h-full"
                        poster={workout.thumbnailUrl}
                    >
                        Your browser does not support the video tag.
                    </video>
                </div>

                {workout.description && (
                    <div className="p-4">
                        <p className={`${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                            {workout.description}
                        </p>
                    </div>
                )}
            </div>
        </div>
    );

    if (loading) {
        return (
            <div className="max-w-7xl mx-auto px-4 py-8">
                <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'} mb-6`}>🎥 Workout Videos</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[...Array(6)].map((_, i) => (
                        <div key={i} className={`${darkMode ? 'bg-gray-800' : 'bg-white'} rounded-lg shadow animate-pulse`}>
                            <div className="aspect-video bg-gray-300 dark:bg-gray-700"></div>
                            <div className="p-4">
                                <div className="h-4 bg-gray-300 dark:bg-gray-700 rounded mb-2"></div>
                                <div className="h-3 bg-gray-300 dark:bg-gray-700 rounded"></div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="flex justify-between items-center mb-6">
                <h2 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>🎥 Workout Videos</h2>
                {isAdmin && (
                    <div className={`text-sm px-3 py-1 rounded-full bg-purple-100 text-purple-600 dark:bg-purple-900 dark:text-purple-300`}>
                        Admin Mode
                    </div>
                )}
            </div>

            {/* Category Filter */}
            <div className="mb-8">
                <div className="flex flex-wrap gap-2">
                    {categories.map((category) => (
                        <button
                            key={category}
                            onClick={() => setSelectedCategory(category)}
                            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${selectedCategory === category
                                ? 'bg-blue-600 text-white'
                                : darkMode
                                    ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                                }`}
                        >
                            {category.charAt(0).toUpperCase() + category.slice(1)}
                        </button>
                    ))}
                </div>
            </div>

            {/* Workout Grid */}
            {filteredWorkouts.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {filteredWorkouts.map((workout) => (
                        <WorkoutCard key={workout.id} workout={workout} />
                    ))}
                </div>
            ) : (
                <div className={`text-center py-12 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    <div className="text-6xl mb-4">🎥</div>
                    <p className="text-xl mb-2">No workout videos available</p>
                    <p>Check back soon for new content!</p>
                </div>
            )}

            {/* Video Modal */}
            {playingVideo && (
                <VideoModal
                    workout={workouts.find(w => w.id === playingVideo)}
                    onClose={handleCloseVideo}
                />
            )}
        </div>
    );
};

export default Workouts;